import { parseDateKey } from "./date";

export const DAY_NAMES_FR = [
  "dim.",
  "lun.",
  "mar.",
  "mer.",
  "jeu.",
  "ven.",
  "sam.",
] as const;

export const MONTH_NAMES_FR = [
  "janv.",
  "févr.",
  "mars",
  "avr.",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
] as const;

export function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function formatTimeFromMs(ms: number): string {
  if (!ms) return "-";
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface DurationFormat {
  long: string;
  short: string;
  hm: string;
  hms: string;
}

export function formatDuration(ms: number): DurationFormat {
  const safe = ms < 0 ? 0 : ms;
  const s = Math.floor(safe / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return {
    long: `${h} h ${pad(m)} min`,
    short: `${h}h${pad(m)}`,
    hm: `${pad(h)}:${pad(m)}`,
    hms: `${pad(h)}:${pad(m)}:${pad(sec)}`,
  };
}

/** Durée en minutes, forme courte (« 4h00 »). */
export function formatMinutes(min: number): string {
  return formatDuration(min * 60000).short;
}

export function formatDateLong(d: Date): string {
  const dn = DAY_NAMES_FR[d.getDay()] ?? "";
  const mn = MONTH_NAMES_FR[d.getMonth()] ?? "";
  return `${dn} ${d.getDate()} ${mn} ${d.getFullYear()}`;
}

// Date complète capitalisée : "Mardi 23 juin 2026".
export function formatDateFull(d: Date): string {
  const s = d.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Période « 21 sept. → 27 sept. 2026 » (bornes ISO, jour pris tel quel). */
export function formatPeriodRange(startISO: string, endISO: string): string {
  const s = parseDateKey(startISO.slice(0, 10));
  const e = parseDateKey(endISO.slice(0, 10));
  return `${s.getDate()} ${MONTH_NAMES_FR[s.getMonth()] ?? ""} → ${e.getDate()} ${MONTH_NAMES_FR[e.getMonth()] ?? ""} ${e.getFullYear()}`;
}

/** Heure « HH:MM » locale. */
/** Marque du pluriel : "s" au-delà de 1. */
/** « CODE · Libellé », ou le libellé seul sans code. */
export function codeLabel(
  code: string | null | undefined,
  label: string,
): string {
  return code ? `${code} · ${label}` : label;
}

export function plural(n: number): string {
  return n > 1 ? "s" : "";
}

export function formatClock(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Jour d'une réception, à placer devant l'heure : "" aujourd'hui, "hier ",
 * sinon "le 27/09 ". */
export function receivedDayPrefix(d: Date): string {
  const rel = relativeDayLabel(d);
  if (rel === "aujourd'hui") return "";
  if (rel === "hier") return "hier ";
  return `le ${pad(d.getDate())}/${pad(d.getMonth() + 1)} `;
}

// Suffixe relatif court : "aujourd'hui" | "hier" | "avant-hier" | "".
export function relativeDayLabel(d: Date): string {
  const now = new Date();
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((b - a) / 86400000);
  if (diff === 0) return "aujourd'hui";
  if (diff === 1) return "hier";
  if (diff === 2) return "avant-hier";
  return "";
}
